import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  message,
  Popconfirm,
  Card,
  Empty,
  Tabs,
  Alert,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import {
  getCreditCards,
  createCreditCard,
  updateCreditCard,
  deleteCreditCard,
  getBankAccounts,
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
  type CreditCard,
  type BankAccount,
} from '../api/budget';

const CreditCards: React.FC = () => {
  const [creditCards, setCreditCards] = useState<CreditCard[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState<'creditcard' | 'bankaccount'>('creditcard');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const loadCreditCards = async () => {
    setLoading(true);
    try {
      const resp = await getCreditCards();
      if (resp.ok && resp.data) setCreditCards(resp.data);
    } catch (error) {
      message.error('Failed to load credit cards');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadBankAccounts = async () => {
    setLoading(true);
    try {
      const resp = await getBankAccounts();
      if (resp.ok && resp.data) setBankAccounts(resp.data);
    } catch (error) {
      message.error('Failed to load bank accounts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreditCards();
    loadBankAccounts();
  }, []);

  const handleAddCreditCard = () => {
    setModalType('creditcard');
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditCreditCard = (record: CreditCard) => {
    setModalType('creditcard');
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      last_four: record.last_four,
      credit_limit: record.credit_limit,
      billing_day: record.billing_day,
      annual_fee: record.annual_fee,
      fee_month: record.fee_month,
      waiver_days_before: record.waiver_days_before,
    });
    setModalVisible(true);
  };

  const handleAddBankAccount = () => {
    setModalType('bankaccount');
    setEditingId(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditBankAccount = (record: BankAccount) => {
    setModalType('bankaccount');
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      account_number: record.account_number,
      balance: record.balance,
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (modalType === 'creditcard') {
        if (editingId) {
          await updateCreditCard(editingId, values);
          message.success('Credit card updated successfully');
        } else {
          await createCreditCard(values);
          message.success('Credit card added successfully');
        }
        loadCreditCards();
      } else {
        if (editingId) {
          await updateBankAccount(editingId, values);
          message.success('Bank account updated successfully');
        } else {
          await createBankAccount(values);
          message.success('Bank account added successfully');
        }
        loadBankAccounts();
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error(
        modalType === 'creditcard'
          ? editingId
            ? 'Failed to update credit card'
            : 'Failed to add credit card'
          : editingId
            ? 'Failed to update bank account'
            : 'Failed to add bank account'
      );
      console.error(error);
    }
  };

  const handleDeleteCreditCard = async (id: string) => {
    try {
      await deleteCreditCard(id);
      message.success('Credit card deleted successfully');
      loadCreditCards();
    } catch (error) {
      message.error('Failed to delete credit card');
      console.error(error);
    }
  };

  const handleDeleteBankAccount = async (id: string) => {
    try {
      await deleteBankAccount(id);
      message.success('Bank account deleted successfully');
      loadBankAccounts();
    } catch (error) {
      message.error('Failed to delete bank account');
      console.error(error);
    }
  };

  const creditCardColumns = [
    {
      title: 'Card Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Last Four',
      dataIndex: 'last_four',
      key: 'last_four',
      width: 120,
      render: (lastFour: string) => `****${lastFour}`,
    },
    {
      title: 'Credit Limit',
      dataIndex: 'credit_limit',
      key: 'credit_limit',
      width: 120,
      align: 'right' as const,
      render: (limit: number) => `$${limit.toLocaleString()}`,
    },
    {
      title: 'Billing Day',
      dataIndex: 'billing_day',
      key: 'billing_day',
      width: 100,
      render: (day: number) => `${day}${getOrdinalSuffix(day)}`,
    },
    {
      title: 'Annual Fee',
      dataIndex: 'annual_fee',
      key: 'annual_fee',
      width: 110,
      align: 'right' as const,
      render: (fee: number) => (fee > 0 ? `$${fee.toFixed(2)}` : '-'),
    },
    {
      title: 'Fee Month',
      dataIndex: 'fee_month',
      key: 'fee_month',
      width: 100,
      render: (month: number) => {
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        return month ? monthNames[month - 1] || '-' : '-';
      },
    },
    {
      title: 'Waiver Days',
      dataIndex: 'waiver_days_before',
      key: 'waiver_days_before',
      width: 100,
      render: (days: number) => days || '-',
    },
    {
      title: 'Alert',
      key: 'alert',
      width: 80,
      render: (_: unknown, record: CreditCard) => {
        if (record.annual_fee > 0 && record.fee_month) {
          return (
            <AlertOutlined
              style={{ color: '#ff7875', fontSize: '16px' }}
              title="Annual fee tracking"
            />
          );
        }
        return '-';
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: CreditCard) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCreditCard(record)}
          />
          <Popconfirm
            title="Delete Credit Card"
            description="Are you sure you want to delete this credit card?"
            onConfirm={() => handleDeleteCreditCard(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const bankAccountColumns = [
    {
      title: 'Account Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: 'Account Number',
      dataIndex: 'account_number',
      key: 'account_number',
      width: 150,
      render: (accountNumber: string) => {
        if (!accountNumber) return '-';
        const last4 = accountNumber.slice(-4);
        return `****${last4}`;
      },
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      align: 'right' as const,
      sorter: (a: BankAccount, b: BankAccount) => a.balance - b.balance,
      render: (balance: number) => (
        <span style={{ fontWeight: 'bold' }}>${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: BankAccount) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditBankAccount(record)}
          />
          <Popconfirm
            title="Delete Bank Account"
            description="Are you sure you want to delete this bank account?"
            onConfirm={() => handleDeleteBankAccount(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const creditCardModalContent = (
    <Form form={form} layout="vertical">
      <Form.Item
        label="Card Name"
        name="name"
        rules={[{ required: true, message: 'Please enter a card name' }]}
      >
        <Input placeholder="e.g., Chase Sapphire Reserve" />
      </Form.Item>

      <Form.Item
        label="Last Four Digits"
        name="last_four"
        rules={[
          { required: true, message: 'Please enter last four digits' },
          {
            pattern: /^\d{4}$/,
            message: 'Please enter exactly 4 digits',
          },
        ]}
      >
        <Input maxLength={4} placeholder="1234" />
      </Form.Item>

      <Form.Item
        label="Credit Limit"
        name="credit_limit"
        rules={[{ required: true, message: 'Please enter credit limit' }]}
      >
        <InputNumber
          min={0}
          step={100}
          style={{ width: '100%' }}
          prefix="$"
        />
      </Form.Item>

      <Form.Item
        label="Billing Day"
        name="billing_day"
        rules={[{ required: true, message: 'Please enter billing day' }]}
      >
        <InputNumber min={1} max={31} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item label="Annual Fee" name="annual_fee" initialValue={0}>
        <InputNumber
          min={0}
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          prefix="$"
        />
      </Form.Item>

      <Form.Item label="Fee Month" name="fee_month">
        <Select placeholder="Select month for annual fee">
          <Select.Option value={1}>January</Select.Option>
          <Select.Option value={2}>February</Select.Option>
          <Select.Option value={3}>March</Select.Option>
          <Select.Option value={4}>April</Select.Option>
          <Select.Option value={5}>May</Select.Option>
          <Select.Option value={6}>June</Select.Option>
          <Select.Option value={7}>July</Select.Option>
          <Select.Option value={8}>August</Select.Option>
          <Select.Option value={9}>September</Select.Option>
          <Select.Option value={10}>October</Select.Option>
          <Select.Option value={11}>November</Select.Option>
          <Select.Option value={12}>December</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item label="Waiver Days" name="waiver_days_before" initialValue={0}>
        <InputNumber
          min={0}
          step={1}
          style={{ width: '100%' }}
          placeholder="Days before fee due date to waive"
        />
      </Form.Item>
    </Form>
  );

  const bankAccountModalContent = (
    <Form form={form} layout="vertical">
      <Form.Item
        label="Account Name"
        name="name"
        rules={[{ required: true, message: 'Please enter an account name' }]}
      >
        <Input placeholder="e.g., Checking - Chase" />
      </Form.Item>

      <Form.Item
        label="Account Number"
        name="account_number"
        rules={[{ required: true, message: 'Please enter account number' }]}
      >
        <Input placeholder="Full or partial account number" />
      </Form.Item>

      <Form.Item
        label="Balance"
        name="balance"
        rules={[{ required: true, message: 'Please enter balance' }]}
      >
        <InputNumber
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          prefix="$"
        />
      </Form.Item>
    </Form>
  );

  const tabItems = [
    {
      key: 'creditcards',
      label: 'Credit Cards',
      children: (
        <Card
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCreditCard}
            >
              Add Credit Card
            </Button>
          }
        >
          <Table
            columns={creditCardColumns}
            dataSource={creditCards}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="No credit cards" /> }}
            scroll={{ x: 1000 }}
          />
        </Card>
      ),
    },
    {
      key: 'bankaccounts',
      label: 'Bank Accounts',
      children: (
        <Card
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddBankAccount}
            >
              Add Bank Account
            </Button>
          }
        >
          <Table
            columns={bankAccountColumns}
            dataSource={bankAccounts}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="No bank accounts" /> }}
            scroll={{ x: 800 }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div className="credit-cards-page">
      <Tabs items={tabItems} />

      <Modal
        title={
          modalType === 'creditcard'
            ? editingId
              ? 'Edit Credit Card'
              : 'Add Credit Card'
            : editingId
              ? 'Edit Bank Account'
              : 'Add Bank Account'
        }
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={500}
      >
        {modalType === 'creditcard' ? creditCardModalContent : bankAccountModalContent}
      </Modal>
    </div>
  );
};

function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) {
    return 'st';
  }
  if (j === 2 && k !== 12) {
    return 'nd';
  }
  if (j === 3 && k !== 13) {
    return 'rd';
  }
  return 'th';
}

export default CreditCards;
